import { uniq } from 'lodash'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import i18n from 'modules/core/lib/i18n'

import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useDestroyRestDays } from 'modules/shift_management/hooks/useDestroyRestDays'
import { useDestroyShifts } from 'modules/shift_management/hooks/useDestroyShifts'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionDispatch } from 'modules/shift_management/hooks/useSelection'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import getTransactionType from 'modules/shift_management/lib/getTransactionType'
import shiftManagementCounts from 'modules/shift_management/lib/shiftManagementCounts'
import { trackBulkDeleteEvent } from 'modules/shift_management/lib/trackDeleteEvent'
import type { RestDay, Shift, ShiftsRow } from 'modules/shift_management/lib/types'
import restDaysCollection from 'modules/shift_management/store/api/collections/restDays'
import shiftsCollection from 'modules/shift_management/store/api/collections/shifts'

interface Props {
  shifts: Shift[]
  restDays: RestDay[]
  destroyBackUpShifts: boolean
}

const useBulkDestroy = ({ shifts, restDays, destroyBackUpShifts }: Props) => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ view, shiftsConflicts }] = useSchedule()
  const dispatchSelection = useSelectionDispatch()
  const { refreshStats } = useRefreshStats(view.startAt, view.endAt)

  const { shiftsCount, restDaysCount, employeeCount } = shiftManagementCounts(shifts, restDays)
  const transactionType = getTransactionType(shiftsCount, restDaysCount)

  const { onDestroy: onDestroyShifts, submitting: submittingShifts } = useDestroyShifts({
    shifts,
    employeeCount,
    destroyBackUpShifts,
  })
  const { onDestroy: onDestroyRestDays, submitting: submittingRestDays } = useDestroyRestDays(
    restDays,
    employeeCount
  )

  const shiftIds = uniq(shifts.map((shift) => shift.id))
  const restDayIds = restDays.map((restDay) => String(restDay.id))

  const { running: submitting, performAction } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      trackBulkDeleteEvent(shifts, restDays, view, employeeCount, shiftsConflicts, 'top_bar_button')

      await restDaysCollection.rpc('bulk_destroy', { ids: restDayIds })
      await shiftsCollection.rpc('bulk_destroy', {
        ids: shiftIds,
        destroy_backup_shifts: destroyBackUpShifts,
      })

      // Get shiftsRows where there are affected shifts to update stats
      const shiftKeys = new Set(uniq(shifts.map((shift) => shift.rowKeyReference(view))))
      const restDayKeys = new Set(restDayIds)
      const previousShiftsRowsFromShifts = shiftsRows.filter((row: ShiftsRow) =>
        shiftKeys.has(row.key)
      )
      const previousShiftsRowsFromRestDays = shiftsRows.filter((row: ShiftsRow) =>
        restDayKeys.has(row.key)
      )

      const previousShiftsRows = uniq([
        ...previousShiftsRowsFromRestDays,
        ...previousShiftsRowsFromShifts,
      ])

      await refreshStats(previousShiftsRows)

      const shiftsToRefresh = destroyBackUpShifts
        ? shifts
        : shifts.filter((shift) => !shift.isBackup)

      dispatchShiftsRows({ type: 'DESTROY_SHIFTS', shifts: shiftsToRefresh, view })
      dispatchShiftsRows({ type: 'DESTROY_REST_DAYS', restDays })
      dispatchSelection({ type: 'CLEAR_SELECTION' })
      closePortal()
    },
    {
      successMessage: `${i18n.t('shift_management.sidepanel.rest_days')} ${i18n.t(
        'shift_management.add_shift.and'
      )} ${i18n.t('shift_management.actions.delete_shifts_success').toLowerCase()}`,
    }
  )

  switch (transactionType) {
    case 'shifts':
      return { onDestroy: onDestroyShifts, submitting: submittingShifts }
    case 'rest-days':
      return { onDestroy: onDestroyRestDays, submitting: submittingRestDays }
    case 'both':
      return { onDestroy: performAction, submitting }
  }
}

export default useBulkDestroy
